/**
 * Contractor Insurance Quote Wizard — Version H2
 * Mirrors Motorcycle_H2:
 * - Dual view (Wizard / Vertical)
 * - JSX externalized copy (contractorCopy.tsx)
 * - Section success only when toggles are ON for Workers’ Comp and Commercial Auto
 * - DEMO_MODE (independent of TEST_MODE) + optional UI toggle
 */

import React, { useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, ProgressBar, Row } from "react-bootstrap";
import { CONTRACTOR_COPY, IContractorCopy } from "./Contractor-insurance-quote-v01B-example-strings";

/* ============================== CONFIG ============================== */
const VERSION = "Contractor-H2";
const SHOW_VERSION = true;

const TEST_MODE = false;
const API_GATEWAY_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";

const DEMO_MODE_DEFAULT = true;
const DEMO_MODE_UI_ENABLED = true;

type ViewMode = "wizard" | "vertical";
type FormShape = Record<string, unknown>;
type SetForm = (patch: FormShape) => void;

export interface IWizardSection {
  id: string;
  title: React.ReactNode;
  explanation: React.ReactNode;
  enabled?: boolean;
  render: (form: FormShape, setForm: SetForm) => React.ReactNode;
}

const clampPct = (p: number) => Math.min(99, Math.max(0, Math.round(p)));

/* ============================== DATA ============================== */
const US_STATES: { value: string; label: string }[] = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" }, { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" }, { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" }, { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" }, { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" }, { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" }, { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" }, { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" }, { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" }, { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" }, { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" }, { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" }, { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" }, { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" }, { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" }, { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

/* ============================== HELPERS ============================== */
const S = (v: unknown): string | null => {
  if (v == null) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t === "" ? null : t;
  }
  return String(v);
};
const hasVal = (v: unknown): boolean => (typeof v === "string" ? v.trim().length > 0 : v != null);

/* ============================== COMPLETION SCHEMA ============================== */
type SectionKeys = {
  id: string;
  required: (form: FormShape) => string[];
  optional?: (form: FormShape) => string[];
};

const completionSchema: SectionKeys[] = [
  {
    id: "business",
    required: () => [
      "legalName", "yearsInBusiness", "primaryTrade", "state",
      "employees", "annualRevenue",
    ],
  },
  {
    id: "lic-history",
    required: () => ["licenseState", "claimsLast3Yrs", "safetyProgram"],
  },
  {
    id: "workers-comp",
    required: (form) => {
      const need = ((form.needWc as string) ?? "no") === "yes";
      return need ? ["payroll", "wcClassCode"] : [];
    },
  },
  {
    id: "commercial-auto",
    required: (form) => {
      const has = ((form.hasVehicles as string) ?? "no") === "yes";
      return has ? ["vehicleCount", "radius", "hasLaddersRacks"] : [];
    },
  },
  {
    id: "coverage",
    required: () => [],
    optional: () => ["glLimit", "addtlInsureds"],
  },
  {
    id: "contact",
    required: () => ["firstName", "lastName", "email", "phone"],
  },
];

const requiredKeysForSection = (sectionId: string, form: FormShape): string[] =>
  completionSchema.find((s) => s.id === sectionId)?.required(form) ?? [];

const optionalKeysForSection = (sectionId: string, form: FormShape): string[] =>
  completionSchema.find((s) => s.id === sectionId)?.optional?.(form) ?? [];

const sectionIsComplete = (sectionId: string, form: FormShape): boolean => {
  const req = requiredKeysForSection(sectionId, form);
  if (req.length > 0) return req.every((k) => hasVal(form[k]));
  const opt = optionalKeysForSection(sectionId, form);
  return opt.length > 0 && opt.some((k) => hasVal(form[k]));
};

const overallCompletion = (enabledSectionIds: string[], form: FormShape): number => {
  const allReq: string[] = enabledSectionIds.flatMap((id) => requiredKeysForSection(id, form));
  const uniq = Array.from(new Set(allReq));
  if (uniq.length === 0) return 0;
  const filled = uniq.reduce((acc, k) => (hasVal(form[k]) ? acc + 1 : acc), 0);
  return (filled / uniq.length) * 100;
};

/* ============================== CONTROLS ============================== */
const WizardControls: React.FC<{
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
  isFirst: boolean;
  isLast: boolean;
  submitting?: boolean;
  copy: IContractorCopy;
}> = ({ onPrev, onNext, onSubmit, isFirst, isLast, submitting, copy }) => (
  <div className="d-flex justify-content-between">
    <Button variant="secondary" onClick={onPrev} disabled={isFirst || !!submitting}>
      {copy.buttons.previous}
    </Button>
    {isLast ? (
      <Button variant="success" onClick={onSubmit} disabled={!!submitting}>
        {submitting ? "Processing..." : copy.buttons.submit}
      </Button>
    ) : (
      <Button variant="primary" onClick={onNext} disabled={!!submitting}>
        {copy.buttons.next}
      </Button>
    )}
  </div>
);

/* ============================== WIZARD CORE ============================== */
export const DualViewWizard_Contractor_H2: React.FC<{
  mode?: ViewMode;
  sections: IWizardSection[];
  initialForm?: FormShape;
  className?: string;
  onSubmit?: (form: FormShape) => void;
  submitting?: boolean;
  submitted?: boolean;
  copy?: IContractorCopy;
}> = ({
  mode = "wizard",
  sections,
  initialForm = {},
  className,
  onSubmit,
  submitting = false,
  submitted = false,
  copy = CONTRACTOR_COPY,
}) => {
  const enabledSections = useMemo(
    () => sections.filter((s) => s.enabled !== false),
    [sections]
  );

  const [form, setFormState] = useState<FormShape>(initialForm);
  const setForm: SetForm = (patch) => setFormState((prev) => ({ ...prev, ...patch }));

  const [current, setCurrent] = useState(0);
  const [activeId, setActiveId] = useState<string>(enabledSections[0]?.id ?? "");

  const ids = useMemo(() => enabledSections.map((s) => s.id), [enabledSections]);
  const activeIndex = Math.max(0, ids.indexOf(activeId));
  const total = Math.max(1, enabledSections.length);
  const enabledIds = enabledSections.map((s) => s.id);

  const rawPct = overallCompletion(enabledIds, form);
  const pctLabel = submitted ? 100 : clampPct(rawPct);
  const pct = submitted ? 100 : clampPct(rawPct);

  // Success badges mimic Motorcycle_H2 (H11a pattern):
  // show success only if toggles are ON
  const sectionShowsSuccess = (sectionId: string): boolean => {
    const isComplete = sectionIsComplete(sectionId, form);
    if (sectionId === "workers-comp") {
      const need = ((form.needWc as string) ?? "no") === "yes";
      return need && isComplete;
    }
    if (sectionId === "commercial-auto") {
      const has = ((form.hasVehicles as string) ?? "no") === "yes";
      return has && isComplete;
    }
    return isComplete;
  };

  if (mode === "vertical") {
    return (
      <div className={className}>
        <Card className="position-sticky mb-3" style={{ top: 16, zIndex: 1 }}>
          <Card.Header>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <strong>
                  {copy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""} · Section {activeIndex + 1} of {total}
                </strong>{" "}
                — {enabledSections[activeIndex]?.title}
              </div>
              <div style={{ minWidth: 180 }}>
                <ProgressBar now={pct} label={`${pctLabel}%`} />
              </div>
            </div>
          </Card.Header>
        </Card>

        {enabledSections.map((s) => {
          const isActive = activeId === s.id;
          const successVisual = sectionShowsSuccess(s.id);

          const cardClasses = [
            "border",
            successVisual ? "border-success bg-success-subtle" : "",
            isActive && !successVisual ? "border-primary" : "",
          ].join(" ").trim();

          const headerClasses = isActive ? "bg-primary text-white" : "";

          return (
            <Row key={s.id} className="mb-3" style={{ scrollMarginTop: 90 }}>
              <Col
                md={8}
                onClick={() => setActiveId(s.id)}
                onFocusCapture={() => setActiveId(s.id)}
              >
                <Card className={cardClasses}>
                  <Card.Header className={headerClasses}>
                    {s.title}
                    {successVisual && !isActive && <Badge bg="success" className="ms-2">{copy.buttons.completeBadge}</Badge>}
                    {successVisual && isActive && <Badge bg="light" text="dark" className="ms-2">{copy.buttons.completeBadge}</Badge>}
                  </Card.Header>
                  <Card.Body>{s.render(form, setForm)}</Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                <Card className={isActive ? "border border-primary" : "border"}>
                  <Card.Header className={isActive ? "bg-primary text-white" : ""}>
                    {copy.sectionHelpHeader}
                  </Card.Header>
                  <Card.Body>
                    <strong>{s.title}</strong>
                    <p className="mb-0">{s.explanation}</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          );
        })}

        <div className="d-flex justify-content-end mt-3">
          <Button variant="success" onClick={() => onSubmit?.(form)} disabled={!!submitting}>
            {submitting ? "Processing..." : copy.buttons.submit}
          </Button>
        </div>
      </div>
    );
  }

  const step = enabledSections[current];
  const successVisualForStep = sectionShowsSuccess(step.id);
  const cardClasses = successVisualForStep ? "border border-success bg-success-subtle" : "";

  return (
    <Card className={`${className ?? ""} ${cardClasses}`}>
      <Card.Header>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <strong>
              {copy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""} · Step {current + 1} of {total}
            </strong>{" "}
            — {step.title}
          </div>
          <div style={{ minWidth: 160 }}>
            <ProgressBar now={pct} label={`${pctLabel}%`} />
          </div>
        </div>
      </Card.Header>

      <Card.Body>
        {step.render(form, setForm)}

        <Card className="mt-3">
          <Card.Header>{copy.sectionHelpHeader}</Card.Header>
          <Card.Body>
            <strong>{step.title}</strong>
            <p className="mb-0">{step.explanation}</p>
          </Card.Body>
        </Card>

        <hr />
        <WizardControls
          onPrev={() => setCurrent((c) => Math.max(0, c - 1))}
          onNext={() => setCurrent((c) => Math.min(total - 1, c + 1))}
          onSubmit={() => onSubmit?.(form)}
          isFirst={current === 0}
          isLast={current === total - 1}
          submitting={submitting}
          copy={copy}
        />
      </Card.Body>
    </Card>
  );
};

/* ============================== APP SHELL (EXAMPLE USAGE) ============================== */
export const Contractor_QuoteWizard_H2_B: React.FC<{ copy?: IContractorCopy }> = ({ copy = CONTRACTOR_COPY }) => {
  const [uiMode, setUiMode] = useState<ViewMode>("vertical");
  const [demoMode, setDemoMode] = useState<boolean>(DEMO_MODE_DEFAULT);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const initialBase: FormShape = { needWc: "no", hasVehicles: "no" };

  const DEMO_DATA: FormShape = {
    ...initialBase,
    // Business
    legalName: "Acme Contracting LLC",
    dba: "Acme Painting",
    yearsInBusiness: "6",
    primaryTrade: "Painting",
    state: "CO",
    employees: "7",
    annualRevenue: "850000",
    // License & History
    licenseNumber: "CO-PAINT-785921",
    licenseState: "CO",
    claimsLast3Yrs: "0",
    safetyProgram: "yes",
    // Workers' Comp (toggle OFF by default; turn to "yes" to test)
    needWc: "no",
    payroll: "420000",
    wcClassCode: "5403",
    // Commercial Auto (toggle OFF by default; turn to "yes" to test)
    hasVehicles: "no",
    vehicleCount: "3",
    radius: "50",
    hasLaddersRacks: "yes",
    // Coverage
    glLimit: "_1m2m",
    addtlInsureds: "yes",
    // Contact
    firstName: "Jane",
    lastName: "Builder",
    email: "jane.builder@example.com",
    phone: "(720) 555-0133",
  };

  const sections: IWizardSection[] = useMemo(() => {
    const business: IWizardSection = {
      id: "business",
      title: copy.business.title,
      explanation: copy.business.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={8}>
              <Form.Group controlId="legalName">
                <Form.Label>{copy.business.fields.legalName.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.legalName as string) ?? ""}
                  onChange={(e) => setForm({ legalName: e.target.value })}
                  placeholder={copy.business.fields.legalName.placeholder}
                  isValid={hasVal(form.legalName)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="dba">
                <Form.Label>{copy.business.fields.dba.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.dba as string) ?? ""}
                  onChange={(e) => setForm({ dba: e.target.value })}
                  placeholder={copy.business.fields.dba.placeholder}
                  isValid={hasVal(form.dba)}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="yearsInBusiness">
                <Form.Label>{copy.business.fields.yearsInBusiness.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.yearsInBusiness as string) ?? ""}
                  onChange={(e) => setForm({ yearsInBusiness: e.target.value })}
                  placeholder={copy.business.fields.yearsInBusiness.placeholder}
                  inputMode="numeric"
                  isValid={hasVal(form.yearsInBusiness)}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="primaryTrade">
                <Form.Label>{copy.business.fields.primaryTrade.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.primaryTrade as string) ?? ""}
                  onChange={(e) => setForm({ primaryTrade: e.target.value })}
                  placeholder={copy.business.fields.primaryTrade.placeholder}
                  isValid={hasVal(form.primaryTrade)}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="state">
                <Form.Label>{copy.business.fields.state.label}</Form.Label>
                <Form.Select
                  value={(form.state as string) ?? ""}
                  onChange={(e) => setForm({ state: e.target.value })}
                  isValid={hasVal(form.state)}
                >
                  <option value="">{copy.business.fields.state.placeholder}</option>
                  {US_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="employees">
                <Form.Label>{copy.business.fields.employees.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.employees as string) ?? ""}
                  onChange={(e) => setForm({ employees: e.target.value })}
                  placeholder={copy.business.fields.employees.placeholder}
                  inputMode="numeric"
                  isValid={hasVal(form.employees)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="annualRevenue">
                <Form.Label>{copy.business.fields.annualRevenue.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.annualRevenue as string) ?? ""}
                  onChange={(e) => setForm({ annualRevenue: e.target.value })}
                  placeholder={copy.business.fields.annualRevenue.placeholder}
                  inputMode="numeric"
                  isValid={hasVal(form.annualRevenue)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const licHistory: IWizardSection = {
      id: "lic-history",
      title: copy.licHistory.title,
      explanation: copy.licHistory.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="licenseNumber">
                <Form.Label>{copy.licHistory.fields.licenseNumber.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.licenseNumber as string) ?? ""}
                  onChange={(e) => setForm({ licenseNumber: e.target.value })}
                  placeholder={copy.licHistory.fields.licenseNumber.placeholder}
                  isValid={hasVal(form.licenseNumber)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="licenseState">
                <Form.Label>{copy.licHistory.fields.licenseState.label}</Form.Label>
                <Form.Select
                  value={(form.licenseState as string) ?? ""}
                  onChange={(e) => setForm({ licenseState: e.target.value })}
                  isValid={hasVal(form.licenseState)}
                >
                  <option value="">{copy.licHistory.fields.licenseState.placeholder}</option>
                  {US_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="claimsLast3Yrs">
                <Form.Label>{copy.licHistory.fields.claimsLast3Yrs.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.claimsLast3Yrs as string) ?? ""}
                  onChange={(e) => setForm({ claimsLast3Yrs: e.target.value })}
                  placeholder={copy.licHistory.fields.claimsLast3Yrs.placeholder}
                  isValid={hasVal(form.claimsLast3Yrs)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="safetyProgram">
                <Form.Label>{copy.licHistory.fields.safetyProgram.label}</Form.Label>
                <Form.Select
                  value={(form.safetyProgram as string) ?? ""}
                  onChange={(e) => setForm({ safetyProgram: e.target.value })}
                  isValid={hasVal(form.safetyProgram)}
                >
                  <option value="">{copy.licHistory.fields.safetyProgram.placeholder}</option>
                  <option value="yes">{copy.licHistory.fields.safetyProgram.options.yes}</option>
                  <option value="no">{copy.licHistory.fields.safetyProgram.options.no}</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const workersComp: IWizardSection = {
      id: "workers-comp",
      title: copy.workersComp.title,
      explanation: copy.workersComp.help,
      enabled: true, // render always; inner fields gated by toggle
      render: (form, setForm) => {
        const needWc = ((form.needWc as string) ?? "no") === "yes";
        return (
          <Form>
            <Row className="g-3">
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="needWc"
                  label={copy.workersComp.needWcCheckbox}
                  checked={needWc}
                  onChange={(e) => setForm({ needWc: e.target.checked ? "yes" : "no" })}
                />
              </Col>

              {needWc && (
                <>
                  <Col md={6}>
                    <Form.Group controlId="payroll">
                      <Form.Label>{copy.workersComp.fields.payroll.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.payroll as string) ?? ""}
                        onChange={(e) => setForm({ payroll: e.target.value })}
                        placeholder={copy.workersComp.fields.payroll.placeholder}
                        inputMode="numeric"
                        isValid={hasVal(form.payroll)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="wcClassCode">
                      <Form.Label>{copy.workersComp.fields.wcClassCode.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.wcClassCode as string) ?? ""}
                        onChange={(e) => setForm({ wcClassCode: e.target.value })}
                        placeholder={copy.workersComp.fields.wcClassCode.placeholder}
                        isValid={hasVal(form.wcClassCode)}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        );
      },
    };

    const commercialAuto: IWizardSection = {
      id: "commercial-auto",
      title: copy.commercialAuto.title,
      explanation: copy.commercialAuto.help,
      enabled: true,
      render: (form, setForm) => {
        const hasVehicles = ((form.hasVehicles as string) ?? "no") === "yes";
        return (
          <Form>
            <Row className="g-3">
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="hasVehicles"
                  label={copy.commercialAuto.hasVehiclesCheckbox}
                  checked={hasVehicles}
                  onChange={(e) => setForm({ hasVehicles: e.target.checked ? "yes" : "no" })}
                />
              </Col>

              {hasVehicles && (
                <>
                  <Col md={4}>
                    <Form.Group controlId="vehicleCount">
                      <Form.Label>{copy.commercialAuto.fields.vehicleCount.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.vehicleCount as string) ?? ""}
                        onChange={(e) => setForm({ vehicleCount: e.target.value })}
                        placeholder={copy.commercialAuto.fields.vehicleCount.placeholder}
                        inputMode="numeric"
                        isValid={hasVal(form.vehicleCount)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="radius">
                      <Form.Label>{copy.commercialAuto.fields.radius.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.radius as string) ?? ""}
                        onChange={(e) => setForm({ radius: e.target.value })}
                        placeholder={copy.commercialAuto.fields.radius.placeholder}
                        inputMode="numeric"
                        isValid={hasVal(form.radius)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="hasLaddersRacks">
                      <Form.Label>{copy.commercialAuto.fields.hasLaddersRacks.label}</Form.Label>
                      <Form.Select
                        value={(form.hasLaddersRacks as string) ?? ""}
                        onChange={(e) => setForm({ hasLaddersRacks: e.target.value })}
                        isValid={hasVal(form.hasLaddersRacks)}
                      >
                        <option value="">{copy.commercialAuto.fields.hasLaddersRacks.placeholder}</option>
                        <option value="yes">{copy.commercialAuto.fields.hasLaddersRacks.options.yes}</option>
                        <option value="no">{copy.commercialAuto.fields.hasLaddersRacks.options.no}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        );
      },
    };

    const coverage: IWizardSection = {
      id: "coverage",
      title: copy.coverage.title,
      explanation: copy.coverage.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="glLimit">
                <Form.Label>{copy.coverage.fields.glLimit.label}</Form.Label>
                <Form.Select
                  value={(form.glLimit as string) ?? ""}
                  onChange={(e) => setForm({ glLimit: e.target.value })}
                  isValid={hasVal(form.glLimit)}
                >
                  <option value="">{copy.coverage.fields.glLimit.placeholder}</option>
                  <option value="_1m2m">{copy.coverage.fields.glLimit.options._1m2m}</option>
                  <option value="_2m2m">{copy.coverage.fields.glLimit.options._2m2m}</option>
                  <option value="custom">{copy.coverage.fields.glLimit.options.custom}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="addtlInsureds">
                <Form.Label>{copy.coverage.fields.addtlInsureds.label}</Form.Label>
                <Form.Select
                  value={(form.addtlInsureds as string) ?? ""}
                  onChange={(e) => setForm({ addtlInsureds: e.target.value })}
                  isValid={hasVal(form.addtlInsureds)}
                >
                  <option value="">{copy.coverage.fields.addtlInsureds.placeholder}</option>
                  <option value="yes">{copy.coverage.fields.addtlInsureds.options.yes}</option>
                  <option value="no">{copy.coverage.fields.addtlInsureds.options.no}</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const contact: IWizardSection = {
      id: "contact",
      title: copy.contact.title,
      explanation: copy.contact.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="firstName">
                <Form.Label>{copy.contact.fields.firstName.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.firstName as string) ?? ""}
                  onChange={(e) => setForm({ firstName: e.target.value })}
                  placeholder={copy.contact.fields.firstName.placeholder}
                  isValid={hasVal(form.firstName)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="lastName">
                <Form.Label>{copy.contact.fields.lastName.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.lastName as string) ?? ""}
                  onChange={(e) => setForm({ lastName: e.target.value })}
                  placeholder={copy.contact.fields.lastName.placeholder}
                  isValid={hasVal(form.lastName)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="email">
                <Form.Label>{copy.contact.fields.email.label}</Form.Label>
                <Form.Control
                  type="email"
                  value={(form.email as string) ?? ""}
                  onChange={(e) => setForm({ email: e.target.value })}
                  placeholder={copy.contact.fields.email.placeholder}
                  isValid={hasVal(form.email)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="phone">
                <Form.Label>{copy.contact.fields.phone.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.phone as string) ?? ""}
                  onChange={(e) => setForm({ phone: e.target.value })}
                  placeholder={copy.contact.fields.phone.placeholder}
                  inputMode="tel"
                  isValid={hasVal(form.phone)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    return [business, licHistory, workersComp, commercialAuto, coverage, contact];
  }, [copy]);

  const initialForm = demoMode ? DEMO_DATA : initialBase;

  const handleSubmit = async (payload: FormShape) => {
    setSubmitError(null);
    setSubmitting(true);

    const enabledIds = sections.filter(s => s.enabled !== false).map(s => s.id);
    const raw = overallCompletion(enabledIds, payload);
    const pctToSend = clampPct(raw);

    const customer_DATA = {
      productMode: "contractor",
      submittedAtIso: new Date().toISOString(),
      source: `Contractor Quote Wizard — ${VERSION}`,

      // Business
      legalName: S(payload.legalName),
      dba: S(payload.dba),
      yearsInBusiness: S(payload.yearsInBusiness),
      primaryTrade: S(payload.primaryTrade),
      state: S(payload.state),
      employees: S(payload.employees),
      annualRevenue: S(payload.annualRevenue),

      // License & History
      licenseNumber: S(payload.licenseNumber),
      licenseState: S(payload.licenseState),
      claimsLast3Yrs: S(payload.claimsLast3Yrs),
      safetyProgram: S(payload.safetyProgram),

      // Workers’ Comp
      needWc: S(payload.needWc),
      payroll: S(payload.payroll),
      wcClassCode: S(payload.wcClassCode),

      // Commercial Auto
      hasVehicles: S(payload.hasVehicles),
      vehicleCount: S(payload.vehicleCount),
      radius: S(payload.radius),
      hasLaddersRacks: S(payload.hasLaddersRacks),

      // Coverage
      glLimit: S(payload.glLimit),
      addtlInsureds: S(payload.addtlInsureds),

      // Contact
      firstName: S(payload.firstName),
      lastName: S(payload.lastName),
      email: S(payload.email),
      phone: S(payload.phone),

      // Progress metric
      formCompletionPercent: String(pctToSend),

      // Legacy/meta
      domainName: String(window.location.hostname),
      itemCategory: String("Customer.contractor.quote"),
      itemClass: String("quote.request"),
      itemType: String("legacy.contractor"),
      clientVersion: String(VERSION + ".contractor"),
    };

    if (TEST_MODE) {
      console.log("TEST MODE — customer_DATA:", customer_DATA);
      setSubmitting(false);
      return;
    }

    try {
      const resp = await fetch(API_GATEWAY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(customer_DATA),
      });
      if (!resp.ok) throw new Error("network");
      setSubmitted(true);
    } catch {
      setSubmitError("Error: network error, try again in a minute.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container my-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card>
              <Card.Header className="fw-bold">{copy.submitThankYou.header}</Card.Header>
              <Card.Body>
                <p className="mb-0">{copy.submitThankYou.body}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container my-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h3 className="m-0">
          {copy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""}
        </h3>

        <div className="d-flex align-items-center gap-3">
          {DEMO_MODE_UI_ENABLED && (
            <Form.Check
              type="checkbox"
              id="demoModeToggle"
              label={copy.buttons.demoToggle}
              checked={demoMode}
              onChange={(e) => setDemoMode(e.target.checked)}
              disabled={submitting}
            />
          )}

          <div className="d-flex gap-2">
            <Button
              variant={uiMode === "wizard" ? "primary" : "outline-primary"}
              onClick={() => setUiMode("wizard")}
              disabled={submitting}
            >
              {copy.buttons.wizardView}
            </Button>
            <Button
              variant={uiMode === "vertical" ? "primary" : "outline-primary"}
              onClick={() => setUiMode("vertical")}
              disabled={submitting}
            >
              {copy.buttons.verticalView}
            </Button>
          </div>
        </div>
      </div>

      {submitError && <Alert variant="danger" className="mb-3">{submitError}</Alert>}

      <DualViewWizard_Contractor_H2
        key={demoMode ? "demo-on" : "demo-off"}
        mode={uiMode}
        sections={sections}
        initialForm={demoMode ? DEMO_DATA : initialBase}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitted={false}
        copy={copy}
      />
    </div>
  );
};

export default Contractor_QuoteWizard_H2_B;
